import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useAuthStore } from './auth'

interface UserSettings {
  sidebarWidth: number
  sidebarCollapsed: boolean
  helpCollapsed: boolean
  dashboardLayout: string
}

const DEFAULT_SETTINGS: UserSettings = {
  sidebarWidth: 240,
  sidebarCollapsed: false,
  helpCollapsed: false,
  dashboardLayout: 'default'
}

const MIN_SIDEBAR_WIDTH = 180
const MAX_SIDEBAR_WIDTH = 400

function getStorageKey(userId: string): string {
  return `user_settings_${userId || 'guest'}`
}

function loadSettings(userId: string): UserSettings {
  try {
    const stored = localStorage.getItem(getStorageKey(userId))
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) }
    }
  } catch (e) {
    console.warn('Failed to load user settings:', e)
  }
  return { ...DEFAULT_SETTINGS }
}

function saveSettings(userId: string, settings: UserSettings): void {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(settings))
  } catch (e) {
    console.warn('Failed to save user settings:', e)
  }
}

export const useUserSettingsStore = defineStore('userSettings', () => {
  const authStore = useAuthStore()
  
  const sidebarWidth = ref(DEFAULT_SETTINGS.sidebarWidth)
  const sidebarCollapsed = ref(DEFAULT_SETTINGS.sidebarCollapsed)
  const helpCollapsed = ref(DEFAULT_SETTINGS.helpCollapsed)
  const dashboardLayout = ref(DEFAULT_SETTINGS.dashboardLayout)

  function getCurrentUserId(): string {
    return authStore.user?.id || 'guest'
  }

  function loadUserSettings(): void {
    const userId = getCurrentUserId()
    const settings = loadSettings(userId)
    sidebarWidth.value = settings.sidebarWidth
    sidebarCollapsed.value = settings.sidebarCollapsed
    helpCollapsed.value = settings.helpCollapsed
    dashboardLayout.value = settings.dashboardLayout
  }

  function saveUserSettings(): void {
    const userId = getCurrentUserId()
    saveSettings(userId, {
      sidebarWidth: sidebarWidth.value,
      sidebarCollapsed: sidebarCollapsed.value,
      helpCollapsed: helpCollapsed.value,
      dashboardLayout: dashboardLayout.value
    })
  }

  function setSidebarWidth(width: number): void {
    sidebarWidth.value = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, width))
    saveUserSettings()
  }

  function toggleSidebarCollapse(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value
    saveUserSettings()
  }

  function toggleHelpCollapse(): void {
    helpCollapsed.value = !helpCollapsed.value
    saveUserSettings()
  }

  function setDashboardLayout(layout: string): void {
    dashboardLayout.value = layout
    saveUserSettings()
  }

  // Watch for user changes and reload settings
  watch(() => authStore.user?.id, () => {
    loadUserSettings()
  })

  // Initialize settings on store creation
  loadUserSettings()

  return {
    sidebarWidth,
    sidebarCollapsed,
    helpCollapsed,
    dashboardLayout,
    loadUserSettings,
    setSidebarWidth,
    toggleSidebarCollapse,
    toggleHelpCollapse,
    setDashboardLayout,
    MIN_SIDEBAR_WIDTH,
    MAX_SIDEBAR_WIDTH
  }
})

